import { buildEscPosDocument, centerLine } from './escPos';
import { clearPairedPrinter, getPairedPrinter, savePairedPrinter } from './printerSlots';

const PREFERRED_WRITE_CHANNELS = [
  { service: '49535343-fe7d-4ae5-8fa9-9fafd205e455', char: '49535343-8841-43f4-a8d4-ecbe34729bb3' },
  { service: '6e400001-b5a3-f393-e0a9-e50e24dcca9e', char: '6e400002-b5a3-f393-e0a9-e50e24dcca9e' },
  { service: '0000ffe0-0000-1000-8000-00805f9b34fb', char: '0000ffe1-0000-1000-8000-00805f9b34fb' },
];

const OPTIONAL_SERVICES = [
  ...new Set(PREFERRED_WRITE_CHANNELS.flatMap(({ service, char }) => [service, char])),
];

// Estado a nivel de ventana: sobrevive cambios de ruta (Caja <-> Pedidos) y remontajes.
const G = typeof window !== 'undefined' ? (window.__pizasBle = window.__pizasBle || { session: null, devices: {} }) : { session: null, devices: {} };
let activeSession = G.session;
function setSession(s) { activeSession = s; G.session = s; }
let printChain = Promise.resolve();

function withPrintLock(fn) {
  const run = printChain.then(fn, fn);
  printChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

export function isWebBluetoothSupported() {
  return typeof navigator !== 'undefined' && Boolean(navigator.bluetooth);
}

export function isPrinterSessionActive(slot) {
  return Boolean(activeSession && activeSession.slot === slot && activeSession.server?.connected !== false);
}

export function getActivePrinterSlot() {
  return activeSession?.slot || null;
}

async function resolveWriteChannel(server, hints) {
  if (hints?.serviceUuid && hints?.characteristicUuid) {
    try {
      const service = await server.getPrimaryService(hints.serviceUuid);
      const characteristic = await service.getCharacteristic(hints.characteristicUuid);
      if (characteristic.properties.write || characteristic.properties.writeWithoutResponse) {
        return {
          characteristic,
          serviceUuid: hints.serviceUuid,
          characteristicUuid: hints.characteristicUuid,
        };
      }
    } catch {
      /* fallthrough */
    }
  }
  for (const { service, char } of PREFERRED_WRITE_CHANNELS) {
    try {
      const svc = await server.getPrimaryService(service);
      const characteristic = await svc.getCharacteristic(char);
      if (characteristic.properties.write || characteristic.properties.writeWithoutResponse) {
        return { characteristic, serviceUuid: service, characteristicUuid: char };
      }
    } catch {
      /* next */
    }
  }
  const services = await server.getPrimaryServices();
  for (const service of services) {
    const chars = await service.getCharacteristics();
    for (const ch of chars) {
      if (ch.properties.writeWithoutResponse || ch.properties.write) {
        return { characteristic: ch, serviceUuid: service.uuid, characteristicUuid: ch.uuid };
      }
    }
  }
  throw new Error('No se encontró canal de escritura en la impresora');
}

async function openDeviceConnection(slot, device, hints) {
  if (!device.gatt) throw new Error('Dispositivo sin GATT');
  const server = await device.gatt.connect();
  const channel = await resolveWriteChannel(server, hints);
  savePairedPrinter(slot, device, channel);
  G.devices[slot] = device;
  setSession({ slot, deviceId: device.id, server, channel });
  if (!device.__pizasListener) {
    device.__pizasListener = true;
    device.addEventListener?.('gattserverdisconnected', () => {
      if (activeSession?.deviceId === device.id) setSession(null);
    });
  }
  return activeSession;
}

async function requestBluetoothDevice() {
  const bluetooth = navigator.bluetooth;
  if (!bluetooth) throw new Error('Bluetooth no disponible. Usa Chrome en Android o PC.');
  try {
    // Mostrar todos los dispositivos: muchas termicas no anuncian sus servicios y con filtros Chrome dice "No se encontraron dispositivos compatibles".
    return await bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: OPTIONAL_SERVICES,
    });
  } catch (firstErr) {
    if (firstErr?.name === 'NotFoundError') {
      throw new Error('No se eligio impresora. Si no aparece: enciendela, quitala de Windows (Configuracion > Bluetooth) o del telefono, y vuelve a pulsar Emparejar.');
    }
    throw firstErr;
  }
}

export async function pairBluetoothPrinter(slot) {
  const device = await requestBluetoothDevice();
  const paired = getPairedPrinter(slot);
  return openDeviceConnection(slot, device, paired || undefined);
}

export async function unpairBluetoothPrinter(slot) {
  if (activeSession?.slot === slot) {
    try {
      activeSession.server.disconnect();
    } catch {
      /* ignore */
    }
    setSession(null);
  }
  delete G.devices[slot];
  clearPairedPrinter(slot);
}

export async function warmReconnectPrinter(slot, opts = {}) {
  if (!isWebBluetoothSupported()) return false;
  if (isPrinterSessionActive(slot) && !opts.force) return true;
  const paired = getPairedPrinter(slot);
  if (!paired) return false;
  const known = G.devices[slot];
  if (known?.gatt && known.id === paired.deviceId) {
    try {
      await openDeviceConnection(slot, known, paired);
      return true;
    } catch {
      /* sigue */
    }
  }
  const getDevices = navigator.bluetooth?.getDevices?.bind(navigator.bluetooth);
  if (!getDevices) {
    if (opts.force) {
      try {
        await pairBluetoothPrinter(slot);
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }
  try {
    const devices = await getDevices();
    const device = devices.find((d) => d.id === paired.deviceId);
    if (!device?.gatt) return false;
    await openDeviceConnection(slot, device, paired);
    return true;
  } catch {
    return false;
  }
}

async function writeChunks(characteristic, data) {
  const chunkSize = 100;
  for (let i = 0; i < data.length; i += chunkSize) {
    const slice = data.slice(i, i + chunkSize);
    if (characteristic.properties.writeWithoutResponse) {
      await characteristic.writeValueWithoutResponse(slice);
    } else {
      await characteristic.writeValue(slice);
    }
    await new Promise((r) => setTimeout(r, 20));
  }
}

export async function printEscPosLines(slot, lines) {
  return withPrintLock(async () => {
    let session = isPrinterSessionActive(slot) ? activeSession : null;
    if (!session) {
      const ok = await warmReconnectPrinter(slot, { force: true });
      if (!ok) throw new Error('Impresora no conectada. Emparéjala primero.');
      session = activeSession;
    }
    const payload = buildEscPosDocument(lines);
    await writeChunks(session.channel.characteristic, payload);
    return true;
  });
}

export async function printEscPosPayload(slot, payload) {
  return withPrintLock(async () => {
    let session = isPrinterSessionActive(slot) ? activeSession : null;
    if (!session) {
      const ok = await warmReconnectPrinter(slot, { force: true });
      if (!ok) throw new Error('Impresora no conectada');
      session = activeSession;
    }
    await writeChunks(session.channel.characteristic, payload);
    return true;
  });
}

export { centerLine };
